import React, { useState } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Shield, CheckCircle, X, ArrowRight, Mail } from 'lucide-react';
import Button from './ui/Button';
import GradientText from './ui/GradientText';
import SEO from './SEO';

interface TwoFactorSetupProps {
  email: string;
  onEnable2FA: () => Promise<void>;
  onSkip: () => void;
}

const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({
  email,
  onEnable2FA,
  onSkip
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleEnable2FA = async () => {
    setLoading(true);
    setError('');

    try {
      await onEnable2FA();
    } catch (err: any) {
      setError(err.message || t('twoFact.setup.errors.enableFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      <SEO
        title="Setup Two-Factor Authentication - Kexa Premium"
        description="Secure your Kexa Premium account with two-factor authentication"
        keywords="kexa 2fa setup, two factor authentication, account security"
      />

      <section className="py-12">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">
                <GradientText variant="primary">{t('twoFact.setup.title')}</GradientText>
              </h1>
              <p className="text-gray-600 mb-4">
                {t('twoFact.setup.subtitle.verified', { email })}
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Shield className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    {t('twoFact.setup.securityPrompt.title')}
                  </h3>
                  <p className="text-blue-800 text-sm mb-3">
                    {t('twoFact.setup.securityPrompt.description')}
                  </p>
                  <div className="flex items-start space-x-2 text-sm text-blue-700">
                    <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{ email }</span>
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <Button
                type="button"
                variant="primary"
                size="lg"
                className="w-full"
                rightIcon={<ArrowRight size={16} />}
                disabled={loading}
                onClick={handleEnable2FA}
              >
                {loading ? t('twoFact.setup.buttons.enabling') : t('twoFact.setup.buttons.enable')}
              </Button>

              <Button
                type="button"
                variant="ghost"
                size="lg"
                className="w-full"
                disabled={loading}
                onClick={onSkip}
              >
                {t('twoFact.setup.buttons.skip')}
              </Button>
            </div>

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                {t('twoFact.setup.footer.note')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TwoFactorSetup;